/**
 * AiBalanceBanner — cartel global cuando un proveedor de IA se queda sin saldo.
 *
 * El backend detecta el error de saldo, conmuta solo a Claude (más caro) y
 * persiste la marca; este banner la muestra hasta que el proveedor se recupera
 * (el backend sondea cada 15 min y limpia la marca solo al detectar crédito).
 * Polls /api/publisher/dashboard/alerts cada 2 min. No es descartable: mientras
 * el failover esté activo, se está gastando de más — tiene que verse.
 */
import { useEffect, useState } from 'react';

const POLL_MS = 2 * 60_000;

const PROVIDER_LABELS: Record<string, string> = {
  deepseek: 'DeepSeek',
  openai: 'OpenAI',
  google: 'Google',
  perplexity: 'Perplexity',
  anthropic: 'Anthropic',
};

type Mark = { since: number; lastError: string };

export function AiBalanceBanner() {
  const [marks, setMarks] = useState<Record<string, Mark>>({});

  useEffect(() => {
    let aborted = false;
    async function fetchAlerts() {
      try {
        const res = await fetch('/api/publisher/dashboard/alerts', { credentials: 'include' });
        if (!res.ok) return;
        const json = (await res.json()) as {
          success: boolean;
          data?: { ai_balance?: Record<string, Mark> };
        };
        if (!aborted) setMarks(json.data?.ai_balance ?? {});
      } catch {
        // Silencioso — el banner no es crítico
      }
    }
    fetchAlerts();
    const id = setInterval(fetchAlerts, POLL_MS);
    return () => {
      aborted = true;
      clearInterval(id);
    };
  }, []);

  const entries = Object.entries(marks);
  if (entries.length === 0) return null;

  return (
    <div
      className="border-l-4 bg-orange-100 border-orange-400 text-orange-900 px-4 py-2 mb-3 rounded shadow-sm"
      role="alert"
    >
      <div className="font-semibold mb-0.5">Proveedor de IA sin saldo</div>
      {entries.map(([provider, m]) => (
        <div key={provider} className="text-sm">
          <span className="font-medium">{PROVIDER_LABELS[provider] || provider}</span> sin saldo
          desde las{' '}
          {new Date(m.since).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} —
          las tareas están saliendo por Claude (más caro). Recargá el saldo: el sistema vuelve solo
          al detectar crédito (verifica cada 15 minutos).
        </div>
      ))}
    </div>
  );
}
